import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { createLink } from '@tanstack/react-router'
import React from 'react'
import styled from 'styled-components'

export const StyledDropdownMenuTrigger = styled(DropdownMenu.Trigger)`
  cursor: pointer;
`

const StyledContent = styled(DropdownMenu.Content)`
  z-index: 1000;
  width: 240px;
  padding: 0.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 0.5rem;
  box-shadow:
    0 0.625rem 2.375rem -0.625rem rgba(22, 23, 24, 0.35),
    0 0.625rem 1.25rem -0.9375rem rgba(22, 23, 24, 0.2);

  [role='menuitem']:focus-visible,
  [role='menuitemradio']:focus-visible,
  [role='menuitemcheckbox']:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.border};
    outline-offset: -2px;
    border-radius: 0.25rem;
  }
  &[data-nav='pointer'] :focus-visible {
    outline: none !important;
  }
`

/* eslint-disable lingui/no-unlocalized-strings -- KeyboardEvent.key values, not display text. */
const KEYBOARD_NAV_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'Tab',
])
/* eslint-enable lingui/no-unlocalized-strings */

export const StyledDropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenu.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenu.Content>
>(function StyledDropdownMenuContent(
  { onKeyDown, onPointerMove, ...props },
  forwardedRef
) {
  const contentRef = React.useRef<React.ElementRef<
    typeof DropdownMenu.Content
  > | null>(null)

  const setNavMode = (mode: 'keyboard' | 'pointer') => {
    const el = contentRef.current
    if (el && el.getAttribute('data-nav') !== mode) {
      el.setAttribute('data-nav', mode)
    }
  }

  return (
    <StyledContent
      {...props}
      ref={(node) => {
        contentRef.current = node
        if (typeof forwardedRef === 'function') forwardedRef(node)
        else if (forwardedRef) forwardedRef.current = node
      }}
      data-nav='keyboard'
      onKeyDown={(event) => {
        onKeyDown?.(event)
        if (KEYBOARD_NAV_KEYS.has(event.key)) setNavMode('keyboard')
      }}
      onPointerMove={(event) => {
        onPointerMove?.(event)
        setNavMode('pointer')
      }}
    />
  )
})

const StyledAnchor = styled.a`
  display: block;
  padding: 0.25rem 1rem;
  border-radius: 0.25rem;
  text-decoration: none;
  color: ${({ theme }) => theme.forecast.secondaryText};

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.border};
    outline-offset: -2px;
  }
`

type DropdownAnchorProps = React.ComponentPropsWithoutRef<'a'>

const DropdownAnchor = React.forwardRef<HTMLAnchorElement, DropdownAnchorProps>(
  function DropdownAnchor({ children, ...props }, ref) {
    return (
      <DropdownMenu.Item asChild>
        <StyledAnchor ref={ref} {...props}>
          {children}
        </StyledAnchor>
      </DropdownMenu.Item>
    )
  }
)

export const DropdownLink = createLink(DropdownAnchor)
